<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { CSAT_RATINGS } from 'shared/constants/messages';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const accountId = useMapGetter('getCurrentAccountId');
const getAccount = useMapGetter('accounts/getAccount');
const accountUIFlags = useMapGetter('accounts/getUIFlags');

const dialogRef = ref(null);
const alertsEnabled = ref(true);
const emailRows = ref(['']);
const selectedRatings = ref([]);

const open = () => {
  const account = getAccount.value(accountId.value) || {};
  const settings = account.settings || {};
  alertsEnabled.value = settings.csat_alert_enabled !== false;
  const storedEmails = (settings.csat_alert_email || '')
    .split(',')
    .map(email => email.trim())
    .filter(Boolean);
  emailRows.value = storedEmails.length ? storedEmails : [''];
  selectedRatings.value = Array.isArray(settings.csat_alert_ratings)
    ? settings.csat_alert_ratings.map(Number)
    : [];
  dialogRef.value?.open();
};

const addEmailRow = () => {
  emailRows.value = [...emailRows.value, ''];
};

const removeEmailRow = index => {
  if (emailRows.value.length <= 1) {
    emailRows.value = [''];
    return;
  }
  emailRows.value = emailRows.value.filter((_, i) => i !== index);
};

const close = () => {
  dialogRef.value?.close();
};

const allSelected = computed(
  () => selectedRatings.value.length === CSAT_RATINGS.length
);

const toggleAll = () => {
  selectedRatings.value = allSelected.value
    ? []
    : CSAT_RATINGS.map(rating => rating.value);
};

const toggleRating = value => {
  if (selectedRatings.value.includes(value)) {
    selectedRatings.value = selectedRatings.value.filter(v => v !== value);
  } else {
    selectedRatings.value = [...selectedRatings.value, value].sort();
  }
};

const isSaving = computed(() => accountUIFlags.value.isUpdating);

const emailList = computed(() =>
  emailRows.value.map(email => email.trim()).filter(Boolean)
);

const isEmailValid = computed(() =>
  emailList.value.every(email => /.+@.+\..+/.test(email))
);

const onConfirm = async () => {
  try {
    await store.dispatch('accounts/update', {
      csat_alert_enabled: alertsEnabled.value,
      csat_alert_email: emailList.value.join(', '),
      csat_alert_ratings: selectedRatings.value,
    });
    useAlert(t('CSAT_REPORTS.ALERT_SETTINGS.API.SUCCESS'));
    close();
  } catch (error) {
    useAlert(t('CSAT_REPORTS.ALERT_SETTINGS.API.ERROR'));
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('CSAT_REPORTS.ALERT_SETTINGS.TITLE')"
    :description="t('CSAT_REPORTS.ALERT_SETTINGS.DESCRIPTION')"
    :confirm-button-label="t('CSAT_REPORTS.ALERT_SETTINGS.SAVE')"
    :cancel-button-label="t('CSAT_REPORTS.ALERT_SETTINGS.CANCEL')"
    :is-loading="isSaving"
    :disable-confirm-button="!isEmailValid"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-5">
      <div
        class="flex items-center justify-between gap-3 rounded-lg border border-n-slate-4 bg-n-alpha-1 px-3 py-2.5"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CSAT_REPORTS.ALERT_SETTINGS.ENABLED_LABEL') }}
        </span>
        <Switch v-model="alertsEnabled" :disabled="isSaving" />
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CSAT_REPORTS.ALERT_SETTINGS.EMAIL_LABEL') }}
        </label>
        <div
          v-for="(row, index) in emailRows"
          :key="index"
          class="flex items-center gap-2"
        >
          <input
            v-model="emailRows[index]"
            type="email"
            class="reset-base h-10 min-w-0 flex-1 rounded-lg border border-n-slate-5 bg-n-solid-1 px-3 py-2 text-sm !text-n-slate-12 ![-webkit-text-fill-color:rgb(var(--slate-12))] placeholder:text-n-slate-10 placeholder:[-webkit-text-fill-color:rgb(var(--slate-10))] outline-none focus:border-n-brand"
            :placeholder="t('CSAT_REPORTS.ALERT_SETTINGS.EMAIL_PLACEHOLDER')"
            :disabled="isSaving"
          />
          <button
            v-if="emailRows.length > 1"
            type="button"
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-n-slate-3 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:opacity-50"
            :title="t('CSAT_REPORTS.ALERT_SETTINGS.REMOVE_EMAIL')"
            :disabled="isSaving"
            @click="removeEmailRow(index)"
          >
            <span class="i-lucide-minus size-4" aria-hidden="true" />
            <span class="sr-only">
              {{ t('CSAT_REPORTS.ALERT_SETTINGS.REMOVE_EMAIL') }}
            </span>
          </button>
        </div>
        <button
          type="button"
          class="inline-flex items-center gap-2 self-start rounded-lg border border-n-slate-3 px-3 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
          :disabled="isSaving"
          @click="addEmailRow"
        >
          <span class="i-lucide-plus size-4" aria-hidden="true" />
          {{ t('CSAT_REPORTS.ALERT_SETTINGS.ADD_EMAIL') }}
        </button>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('CSAT_REPORTS.ALERT_SETTINGS.EMAIL_HELP') }}
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CSAT_REPORTS.ALERT_SETTINGS.RATINGS_LABEL') }}
          </span>
          <Button
            :label="
              allSelected
                ? t('CSAT_REPORTS.ALERT_SETTINGS.CLEAR_ALL')
                : t('CSAT_REPORTS.ALERT_SETTINGS.SELECT_ALL')
            "
            variant="link"
            size="xs"
            :disabled="isSaving"
            @click="toggleAll"
          />
        </div>
        <label
          v-for="rating in CSAT_RATINGS"
          :key="rating.value"
          class="flex cursor-pointer items-center gap-3 rounded-lg border border-n-slate-4 bg-n-alpha-1 px-3 py-2"
        >
          <Checkbox
            :model-value="selectedRatings.includes(rating.value)"
            :disabled="isSaving"
            @update:model-value="toggleRating(rating.value)"
          />
          <span class="text-sm text-n-slate-12">
            {{ rating.emoji }}
            {{
              t('CSAT_REPORTS.ALERT_SETTINGS.RATING_OPTION', {
                rating: rating.value,
              })
            }}
          </span>
        </label>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('CSAT_REPORTS.ALERT_SETTINGS.RATINGS_HELP') }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
